import { useState } from 'react';

import { useSelector } from 'react-redux';

import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';
import { getGroupOptions } from 'components/modules/questions/utils';
import API from 'libs/api';
import { selectWebsite } from 'selectors/website';

export function useFaqsPage(pageId, allGroups = [], pageType) {
  const website = useSelector(selectWebsite);
  const websiteId = website?.id;

  const [assignedGroups, setAssignedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  async function fetchAssignedGroups() {
    if (!websiteId || !pageId || !pageType) return;
    setLoading(true);
    setFetchError(null);
    try {
      const groups = await API.getPageQuestionGroups(websiteId, pageType);
      setAssignedGroups(groups);
    } catch (e) {
      setFetchError('Failed to load assigned groups');
    } finally {
      setLoading(false);
    }
  }

  useAsyncEffect(async () => {
    await fetchAssignedGroups();
  }, [websiteId, pageId, pageType]);

  const assignedIds = assignedGroups.map((g) => g.id);
  const availableGroups = (allGroups || []).filter((g) => !assignedIds.includes(g.id));
  const groupOptions = getGroupOptions(availableGroups);

  async function addGroup(selectedGroupId) {
    if (!selectedGroupId || !pageId) return;
    setSaving(true);
    try {
      const newIds = [...assignedIds, Number(selectedGroupId)];
      const updated = await API.assignPageQuestionGroups(websiteId, pageId, newIds);
      setAssignedGroups(updated);
    } finally {
      setSaving(false);
    }
  }

  async function removeGroup(groupId) {
    setSaving(true);
    try {
      const newIds = assignedIds.filter((id) => id !== groupId);
      const updated = await API.assignPageQuestionGroups(websiteId, pageId, newIds);
      setAssignedGroups(updated);
    } finally {
      setSaving(false);
    }
  }

  return {
    assignedGroups,
    availableGroups,
    groupOptions,
    loading,
    saving,
    fetchError,
    addGroup,
    removeGroup,
  };
}
